
import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Lead, LeadStatus } from './types';
import { LeadPipelineCard } from './LeadPipelineCard';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface LeadPipelineColumnProps {
  stage: { id: LeadStatus; label: string };
  leads: Lead[];
}

const getHeaderColor = (status: string): string => {
  switch (status) {
    case "new": return "bg-gray-100 dark:bg-gray-800/30";
    case "contacted": return "bg-blue-100 dark:bg-blue-900/30";
    case "meeting": return "bg-purple-100 dark:bg-purple-900/30";
    case "proposal": return "bg-amber-100 dark:bg-amber-900/30";
    case "negotiating": return "bg-orange-100 dark:bg-orange-900/30";
    case "won": return "bg-green-100 dark:bg-green-900/30";
    case "lost": return "bg-red-100 dark:bg-red-900/30";
    case "onhold": return "bg-slate-100 dark:bg-slate-800/30";
    default: return "bg-gray-100 dark:bg-gray-800/30";
  }
};

const getBadgeColor = (status: string): string => {
  switch (status) {
    case 'new': return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    case 'contacted': return 'bg-blue-200 text-blue-800 dark:bg-blue-700 dark:text-blue-200';
    case 'meeting': return 'bg-purple-200 text-purple-800 dark:bg-purple-700 dark:text-purple-200';
    case 'proposal': return 'bg-amber-200 text-amber-800 dark:bg-amber-700 dark:text-amber-200';
    case 'negotiating': return 'bg-orange-200 text-orange-800 dark:bg-orange-700 dark:text-orange-200';
    case 'won': return 'bg-green-200 text-green-800 dark:bg-green-700 dark:text-green-200';
    case 'lost': return 'bg-red-200 text-red-800 dark:bg-red-700 dark:text-red-200';
    case 'onhold': return 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200';
    default: return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};


export const LeadPipelineColumn: React.FC<LeadPipelineColumnProps> = ({ stage, leads }) => {
  return (
    <div className="flex-shrink-0 w-72 md:w-80">
      <div className="rounded-lg border bg-card shadow-sm h-full flex flex-col">
        <div className={cn("p-3 border-b", getHeaderColor(stage.id))}>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-foreground">{stage.label}</h3>
            <Badge variant="secondary" className={cn("text-xs", getBadgeColor(stage.id))}>
              {leads.length}
            </Badge>
          </div>
        </div>
        <Droppable droppableId={stage.id} type="lead">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(
                "flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]",
                snapshot.isDraggingOver ? 'bg-muted/50' : 'bg-card'
              )}
            >
              {leads.map((lead, index) => (
                <Draggable key={lead.id} draggableId={lead.id} index={index}>
                  {(providedDraggable) => (
                    <div
                      ref={providedDraggable.innerRef}
                      {...providedDraggable.draggableProps}
                      {...providedDraggable.dragHandleProps}
                    >
                      <LeadPipelineCard lead={lead} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {leads.length === 0 && !snapshot.isDraggingOver && (
                <div className="text-center text-xs text-muted-foreground py-10">
                  Drag leads here
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
};
